import type { Invoice } from '@freelancer-os/shared-types';
import { Badge } from '@freelancer-os/ui/src/components/ui/badge';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@freelancer-os/ui/src/components/ui/table';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useInvoices } from '../../hooks/useInvoices';

export function InvoicesPage() {
  const { t } = useTranslation('invoices');
  const { data: invoices, isLoading } = useInvoices();
  const navigate = useNavigate();

  const getStatusBadge = (status: Invoice['status']) => {
    const label = t(`status.${status}`);
    switch (status) {
      case 'DRAFT':
        return <Badge variant="secondary">{label}</Badge>;
      case 'SENT':
        return (
          <Badge variant="default" className="bg-blue-500">
            {label}
          </Badge>
        );
      case 'PAID':
        return (
          <Badge variant="default" className="bg-green-500">
            {label}
          </Badge>
        );
      case 'OVERDUE':
        return <Badge variant="destructive">{label}</Badge>;
      case 'CANCELLED':
        return <Badge variant="outline">{label}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto w-full">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('list.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('list.subtitle')}</p>
        </div>
        <Link to="/invoices/new">
          <Button className="gap-2">
            <Plus className="h-4 w-4" /> {t('list.create')}
          </Button>
        </Link>
      </div>

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('list.invoiceCode')}</TableHead>
              <TableHead>{t('list.client')}</TableHead>
              <TableHead>{t('list.date')}</TableHead>
              <TableHead>{t('list.dueDate')}</TableHead>
              <TableHead className="text-right">{t('list.amount')}</TableHead>
              <TableHead className="text-center">{t('list.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  {t('list.loading')}
                </TableCell>
              </TableRow>
            ) : invoices?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  {t('list.empty')}
                </TableCell>
              </TableRow>
            ) : (
              invoices?.map((invoice) => (
                <TableRow
                  key={invoice.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                >
                  <TableCell className="font-medium">{invoice.invoiceCode}</TableCell>
                  <TableCell>{invoice.client?.name || t('list.unknownClient')}</TableCell>
                  <TableCell>{new Date(invoice.createdAt).toLocaleDateString('vi-VN')}</TableCell>
                  <TableCell>
                    {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('vi-VN') : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                      invoice.totalAmount,
                    )}
                  </TableCell>
                  <TableCell className="text-center">{getStatusBadge(invoice.status)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
